import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { isAdmin } from '../util/IsLoggin';
import {
  LayoutDashboard,
  FileText,
  ShoppingCart,
  BarChart3,
  Settings,
  Wrench,
  CircleDollarSign,
  MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

const menuItems = [
  { key: '/dashboard', label: 'Thống Kê', icon: LayoutDashboard },
  { key: '/orders', label: 'Đơn Hàng', icon: FileText },
  { key: '/chat', label: 'Hội thoại', icon: MessageSquare },
  { key: '/products', label: 'Sản Phẩm', icon: ShoppingCart },
  { key: '/doisoat', label: 'Đối Soát', icon: CircleDollarSign },
  { key: '/reports', label: 'Báo Cáo', icon: BarChart3 },
  { key: '/setting', label: 'Cấu Hình', icon: Settings },
];

const Sidebar = ({ auth, collapsed, onCollapse, mobileOpen, onMobileOpenChange }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleMenuClick = (key) => {
    if (location.pathname !== key) {
      navigate(key);
    }
    onCollapse(true);
    if (onMobileOpenChange) onMobileOpenChange(false);
  };

  const renderMenuContent = (showLabels = false) => (
    <div className="flex flex-col h-full bg-[#0F172A]">
      {/* Logo / Avatar area */}
      <div className="flex items-center justify-center py-5 px-3 border-b border-white/[0.06]">
        <Avatar className="h-9 w-9 ring-2 ring-blue-500/30">
          <AvatarImage src="/pngegg.png" alt="logo" />
          <AvatarFallback className="bg-blue-600 text-white text-xs font-semibold">BP</AvatarFallback>
        </Avatar>
        {auth.isAuthenticated && !auth.loading && auth.user && (showLabels || !collapsed) && (
          <span className="ml-3 text-sm font-medium text-slate-300 truncate">
            {auth.user.phone}
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 px-2 space-y-0.5">
        {menuItems.map((item) => {
          const isActive = location.pathname === item.key || location.pathname.startsWith(item.key + '/');
          return (
            <Button
              key={item.key}
              variant="ghost"
              onClick={() => handleMenuClick(item.key)}
              className={cn(
                'w-full justify-start rounded-md h-10 text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors duration-150',
                isActive && 'bg-blue-600/15 text-blue-400 hover:bg-blue-600/20 hover:text-blue-300 border-l-2 border-blue-500 rounded-l-none'
              )}
            >
              <item.icon className={cn('h-[18px] w-[18px] shrink-0', (showLabels || !collapsed) && 'mr-3')} />
              {(showLabels || !collapsed) && <span className="text-[13px] font-medium">{item.label}</span>}
            </Button>
          );
        })}
        {isAdmin() && (
          <Button
            key="/admin"
            variant="ghost"
            onClick={() => handleMenuClick('/admin')}
            className={cn(
              'w-full justify-start rounded-md h-10 text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors duration-150',
              location.pathname === '/admin' && 'bg-blue-600/15 text-blue-400 hover:bg-blue-600/20 hover:text-blue-300 border-l-2 border-blue-500 rounded-l-none'
            )}
          >
            <Wrench className={cn('h-[18px] w-[18px] shrink-0', (showLabels || !collapsed) && 'mr-3')} />
            {(showLabels || !collapsed) && <span className="text-[13px] font-medium">Admin</span>}
          </Button>
        )}
      </nav>
    </div>
  );

  return (
    <>
      {/* Mobile Sidebar (Sheet) */}
      <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent side="left" className="bg-[#0F172A] p-0 w-64 border-r-0">
          {renderMenuContent(true)}
        </SheetContent>
      </Sheet>

      {/* Desktop Sidebar */}
      <aside
        className={cn(
          'hidden lg:flex flex-col bg-[#0F172A] text-white transition-all duration-200 ease-in-out border-r border-white/[0.06]',
          collapsed ? 'w-16' : 'w-48'
        )}
      >
        {renderMenuContent()}
      </aside>
    </>
  );
};

Sidebar.propTypes = {
  auth: PropTypes.object.isRequired,
  collapsed: PropTypes.bool,
  onCollapse: PropTypes.func.isRequired,
  mobileOpen: PropTypes.bool,
  onMobileOpenChange: PropTypes.func,
};

const mapStateToProps = (state) => ({
  auth: state.AuthRedux,
});

export default connect(mapStateToProps)(Sidebar);
